import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Loader2, Link2, Wrench, CheckCircle2, AlertTriangle, XCircle, Package, FileText } from "lucide-react";
import api, { fmtDate, money } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_API_BASE;
const CFG = {
  good: { icon: CheckCircle2, cls: "text-emerald-400", label: "Good" },
  attention: { icon: AlertTriangle, cls: "text-amber-400", label: "Needs Attention" },
  critical: { icon: XCircle, cls: "text-rose-400", label: "Critical" },
};

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get(`/reports/${id}`).then(({ data }) => setRep(data)).catch(() => toast.error("Not found")).finally(() => setLoading(false)); }, [id]);

  const shareLink = async () => {
    try {
      const { data } = await api.post(`/reports/${id}/share-link`);
      const url = data.path ? `${API_BASE}${data.path}` : (data.url || "");
      if (!url) { toast.error("Could not create link"); return; }
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        try { copied = document.execCommand("copy"); } catch { copied = false; }
        document.body.removeChild(ta);
      }
      if (copied) toast.success("Link copied! You can now paste this into a text or email for your client.");
      else toast.message("Here is the shareable link", { description: url });
    } catch { toast.error("Failed to create link"); }
  };

  if (loading) return <Layout title="Loading…"><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div></Layout>;
  if (!rep) return <Layout title="Not found"><p className="text-slate-400">Report not found.</p></Layout>;

  const vehicle = [rep.vehicle_year, rep.vehicle_make, rep.vehicle_model].filter(Boolean).join(" ");
  const groups = (rep.items || []).reduce((acc, it) => { (acc[it.system] = acc[it.system] || []).push(it); return acc; }, {});
  const laborRate = user?.labor_rate || 100;
  const itemCost = (it) => Number(it.part_cost || 0) + Number(it.labor_hours || 0) * laborRate;
  const recommendedTotal = (rep.items || []).reduce((s, it) => s + itemCost(it), 0);

  return (
    <Layout
      title={`Health Report ${rep.number || ""}`}
      action={
        <div className="flex gap-2 no-print">
          <Button variant="outline" data-testid="back-reports" onClick={() => navigate("/reports")} className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
          <Button variant="outline" data-testid="build-estimate-button" onClick={() => navigate(`/reports/${id}/estimate`)} className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"><FileText className="h-4 w-4 mr-1.5" /> Build Estimate</Button>
          <Button variant="outline" data-testid="share-report-button" onClick={shareLink} className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"><Link2 className="h-4 w-4 mr-1.5" /> Share Link</Button>
          <Button data-testid="print-report-button" onClick={() => window.print()} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Printer className="h-4 w-4 mr-1.5" /> Print / PDF</Button>
        </div>
      }
    >
      <div className="print-area rounded-xl border border-slate-800 bg-slate-900 p-6 md:p-10 max-w-4xl animate-fade-up">
        <div className="flex justify-between items-start pb-6 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center"><Wrench className="h-5 w-5 text-slate-950" /></div>
            <span className="font-head font-bold text-lg text-slate-50 print-accent">{user?.business_name || user?.name || "My Shop"}</span>
          </div>
          <div className="text-right">
            <p className="font-head font-black text-xl uppercase text-slate-100 print-accent">Vehicle Health Report</p>
            <p className="font-mono text-sm text-slate-400 mt-1">{rep.number}</p>
            <p className="text-sm text-slate-400">{fmtDate(rep.created_at)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Customer</p>
            <p className="font-semibold text-slate-100">{rep.customer_name}</p>
            {rep.customer_business_name && <p className="text-sm text-slate-400">{rep.customer_business_name}</p>}
          </div>
          <div className="sm:text-right">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Vehicle</p>
            <p className="font-semibold text-slate-100">{vehicle || "—"}</p>
            {rep.mileage && <p className="text-sm text-slate-400">{rep.mileage} miles</p>}
            {rep.vehicle_vin && <p className="text-sm text-slate-400">VIN: {rep.vehicle_vin}</p>}
          </div>
        </div>

        <div className="space-y-5">
          {Object.entries(groups).map(([system, list]) => (
            <div key={system}>
              <p className="text-[11px] uppercase tracking-wide text-amber-400 print-accent font-semibold mb-2">{system}</p>
              <div className="space-y-1.5">
                {list.map((it, i) => {
                  const cfg = CFG[it.status] || CFG.good;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-800/60">
                      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cfg.cls}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-100">{it.label}</p>
                        {it.note && <p className="text-xs text-slate-500">{it.note}</p>}
                        {(it.part_name || it.labor_hours) && (
                          <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                            <Package className="h-3 w-3 text-slate-500" />
                            {it.part_name || "Labor"}
                            {it.labor_hours ? ` · ${it.labor_hours}h labor` : ""}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`text-xs font-semibold ${cfg.cls}`}>{cfg.label}</span>
                        {itemCost(it) > 0 && <p className="text-xs text-slate-300 tabular-nums mt-0.5">{money(itemCost(it))}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {recommendedTotal > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between" data-testid="report-recommended-total">
            <p className="text-sm font-semibold text-slate-200 flex items-center gap-1.5"><Wrench className="h-4 w-4 text-amber-400" /> Estimated recommended work</p>
            <p className="font-head font-black text-xl text-amber-400 tabular-nums print-accent">{money(recommendedTotal)}</p>
          </div>
        )}

        {rep.summary && (
          <div className="mt-6 pt-4 border-t border-slate-800">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Technician Summary</p>
            <p className="text-sm text-slate-400 whitespace-pre-wrap">{rep.summary}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
